/*
 * Copyright (c) 2024-2025. The content provided herein is protected by copyright law. Unauthorized use, reproduction, or distribution of this material without proper licensing, knowledge, and contract agreement from the author is strictly prohibited. Any infringement of these rights may result in legal action. For inquiries regarding the use of this content, please contact the author for proper authorization and agreement.
 */
import React from "react";
import {Route, Switch} from "react-router-dom";
import {Helmet} from "react-helmet";
import BookingTimeslots from "pages/Front/Booking/BookingTimeslots";
import BookingPackage from "pages/Front/Booking/BookingPackage";
import BookingGiftCardPaid from "pages/Front/Booking/BookingGiftCardPaid";
import BookingCheckout from "pages/Front/Booking/BookingCheckout";
import BookingCustomer from "pages/Front/Booking/BookingCustomer";
import BookingPaid from "pages/Front/Booking/BookingPaid";
import BookingInquired from "pages/Front/Booking/BookingInquired";
import BookingTerms from "pages/Front/Booking/BookingTerms";
import TermsAndConditions from "pages/Front/Booking/BookingTerms/TermsAndConditions";
import Impressum from "pages/Front/Booking/BookingTerms/Impressum";
import {useTranslation} from "react-i18next";
import {BookingSteps} from "types/enums/BookingSteps";
import useNavigation from "utils/hooks/useNavigation";
import BookingLogin from "../BookingLogin";
import BookingLoginReservations from "pages/Front/Booking/BookingLogin/BookingLoginReservations";
import BookingLoginReservationInvitationGuest
    from "pages/Front/Booking/BookingLogin/BookingLoginReservationInvitationGuest";
import BookingLoginReservationDetailRedesign
    from "pages/Front/Booking/BookingLogin/BookingLoginReservationDetailRedesign";
import BookingOptions from "pages/Front/Booking/BookingOptions";
import BookingTimeslotsNew from "pages/Front/Booking/BookingTimeslotsNew";
import BookingIntroNew from "pages/Front/Booking/BookingIntroNew";
import BookingPackagesNew from "pages/Front/Booking/BookingPackagesNew";
import BookingEventsSubcategory from "pages/Front/Booking/BookingEventsSubcategory";
import BookingUpsells from "pages/Front/Booking/BookingUpsells";
import PackageCategoryDetail from "pages/Front/Booking/components/PackageCategoryDetail/PackageCategoryDetail";
import {PackageCategoryDetailEnum} from "types/enums/PackageCategoryDetailEnum";
import BookingSellerReservations from "pages/Front/Booking/Seller/BookingSellerReservations";
import BookingSellerReservationDetail from "pages/Front/Booking/Seller/BookingSellerReservationDetail";
import BookingLoginReservationDetailUpsells
    from "pages/Front/Booking/BookingLogin/components/BookingLoginReservationDetail/BookingLoginReservationDetailUpsells";
import BookingLoginReservationDetailSummary
    from "pages/Front/Booking/BookingLogin/components/BookingLoginReservationDetail/BookingLoginReservationDetailSummary";
import BookingSellerLoginWithPassword from "pages/Front/Booking/Seller/BookingSellerLoginWithPassword";
import SellerForgottenPassword from "pages/Front/Booking/Seller/SellerForgottenPassword";
import BookingFerienSubcategory from "pages/Front/Booking/BookingFerienSubcategory";
import BookingLoginLayout from "layouts/BookingLoginLayout";
import BookingCompetitions from "pages/Front/Booking/BookingCompetitions";

const BookingPages = (props: {selectTimeslotHandler: (bookingStep: BookingSteps) => void}) => {
    const {t} = useTranslation();
    const navigation = useNavigation();
    return (
        <Switch>
            <Route path="/booking/book/:package_id/:package_name?">
                <Helmet>
                    <title>{navigation.titleText(t("Choose timeslot"))}</title>
                </Helmet>
                <BookingTimeslots selectTimeslotHandler={props.selectTimeslotHandler} />
            </Route>
            {/*<Route path="/booking/upsell/:package_id/:package_name?">*/}
            {/*    <Helmet>*/}
            {/*        <title>{navigation.titleText(t("Extra package"))}</title>*/}
            {/*    </Helmet>*/}
            {/*    <BookingPackageUpsells />*/}
            {/*</Route>*/}
            <Route path="/booking/package/:package_id/:package_name?">
                <Helmet>
                    <title>{navigation.titleText(t("Choose Package"))}</title>
                </Helmet>
                <BookingPackage />
            </Route>
            <Route path="/booking/giftcard/paid/:payment_id/:reference?">
                <Helmet>
                    <title>{navigation.titleText(t("Gift card paid"))}</title>
                </Helmet>
                <BookingGiftCardPaid />
            </Route>
            <Route path="/booking/checkout">
                <Helmet>
                    <title>{navigation.titleText(t("Booking Checkout - Enter your data"))}</title>
                </Helmet>
                <BookingCheckout />
            </Route>
            <Route path="/booking/customer">
                <Helmet>
                    <title>{navigation.titleText(t("Booking Customer - Enter your data"))}</title>
                </Helmet>
                <BookingCustomer company={null} />
            </Route>
            <Route path="/booking/paid/:payment_id/:reference">
                <Helmet>
                    <title>{navigation.titleText(t("Booking payment"))}</title>
                </Helmet>
                <BookingPaid />
            </Route>

            <Route path="/booking/paid-upsells/:payment_id/:reference">
                <Helmet>
                    <title>{navigation.titleText(t("Booking payment"))}</title>
                </Helmet>
                <BookingPaid />
            </Route>
            <Route path="/booking/inquiry/:cart_id/:cart_token">
                <Helmet>
                    <title>{navigation.titleText(t("Booking inquiry"))}</title>
                </Helmet>
                <BookingInquired />
            </Route>
            <Route path="/booking/terms-and-conditions">
                <Helmet>
                    <title>{navigation.titleText(t("Terms and condition"))}</title>
                </Helmet>
                <BookingTerms>
                    <TermsAndConditions />
                </BookingTerms>
            </Route>
            <Route path="/booking/impressum">
                <Helmet>
                    <title>{navigation.titleText(t("Impressum"))}</title>
                </Helmet>
                <BookingTerms>
                    <Impressum />
                </BookingTerms>
            </Route>

            <Route path="/booking/login/reservations/detail/:reservation_id/upsells/:cart_guid/summary">
                <Helmet>
                    <title>{navigation.titleText(t("Reservation detail - summary"))}</title>
                </Helmet>
                <BookingLoginLayout>
                    <BookingLoginReservationDetailSummary />
                </BookingLoginLayout>
            </Route>
            <Route path="/booking/login/reservations/detail/:reservation_id/upsells/:cart_guid">
                <Helmet>
                    <title>{navigation.titleText(t("Reservation detail - upsells"))}</title>
                </Helmet>
                <BookingLoginLayout>
                    <BookingLoginReservationDetailUpsells />
                </BookingLoginLayout>
            </Route>
            <Route path="/booking/login/reservations/detail/:reservation_id">
                <Helmet>
                    <title>{navigation.titleText(t("Reservation detail"))}</title>
                </Helmet>
                <BookingLoginLayout>
                    <BookingLoginReservationDetailRedesign />
                </BookingLoginLayout>
            </Route>
            <Route path="/booking/login/reservations/guest/:reservation_driver_guid">
                <Helmet>
                    <title>{navigation.titleText(t("Reservation invitation"))}</title>
                </Helmet>
                <BookingLoginReservationInvitationGuest />
            </Route>
            <Route path="/booking/login/reservations">
                <Helmet>
                    <title>{navigation.titleText(t("Reservations"))}</title>
                </Helmet>
                <BookingLoginLayout>
                    <BookingLoginReservations />
                </BookingLoginLayout>
            </Route>
            <Route path="/booking/login">
                <Helmet>
                    <title>{navigation.titleText(t("Login"))}</title>
                </Helmet>
                <BookingLogin />
            </Route>

            <Route path="/booking/seller/login">
                <Helmet>
                    <title>{navigation.titleText(t("Seller login"))}</title>
                </Helmet>
                <BookingSellerLoginWithPassword />
            </Route>

            <Route path="/booking/seller/login-with-password">
                <Helmet>
                    <title>{navigation.titleText(t("Seller login"))}</title>
                </Helmet>
                <BookingSellerLoginWithPassword />
            </Route>

            <Route path="/booking/seller/forgotten-password/:access_token?/:app_id?/:m_clk_id?">
                <Helmet>
                    <title>{navigation.titleText(t("Forgotten password"))}</title>
                </Helmet>
                <SellerForgottenPassword />
            </Route>

            <Route path="/booking/seller/reservations/detail/:reservation_id">
                <Helmet>
                    <title>{navigation.titleText(t("Reservation detail"))}</title>
                </Helmet>
                <BookingSellerReservationDetail />
            </Route>
            <Route path="/booking/seller/reservations">
                <Helmet>
                    <title>{navigation.titleText(t("Reservations"))}</title>
                </Helmet>
                <BookingSellerReservations />
            </Route>

            <Route path="/booking/events/subcategory">
                <Helmet>
                    <title>{navigation.titleText(t("Events subcategory"))}</title>
                </Helmet>
                <BookingEventsSubcategory />
            </Route>
            <Route path="/booking/event/detail/:categorySlug">
                <Helmet>
                    <title>{navigation.titleText(t("Events subcategory"))}</title>
                </Helmet>
                <PackageCategoryDetail type={PackageCategoryDetailEnum.EventDetail} />
            </Route>
            <Route path="/booking/ferien/subcategory">
                <Helmet>
                    <title>{navigation.titleText(t("Ferien subcategory"))}</title>
                </Helmet>
                <BookingFerienSubcategory />
            </Route>
            <Route path="/booking/ferien/detail/:categorySlug">
                <Helmet>
                    <title>{navigation.titleText(t("Ferien subcategory"))}</title>
                </Helmet>
                <PackageCategoryDetail type={PackageCategoryDetailEnum.EventDetail} />
            </Route>
            <Route path="/booking/events/detail/">
                <Helmet>
                    <title>{navigation.titleText(t("Events subcategory"))}</title>
                </Helmet>
                <PackageCategoryDetail type={PackageCategoryDetailEnum.Events} />
            </Route>
            <Route path="/booking/category/detail/:categorySlug">
                <Helmet>
                    <title>{navigation.titleText(t("Events subcategory"))}</title>
                </Helmet>
                <PackageCategoryDetail type={PackageCategoryDetailEnum.CategoryDetail} />
            </Route>
            <Route path="/booking/options">
                <Helmet>
                    <title>{navigation.titleText(t("Options"))}</title>
                </Helmet>
                <BookingOptions />
            </Route>
            <Route path="/booking/timeslots">
                <Helmet>
                    <title>{navigation.titleText(t("Booking timeslots"))}</title>
                </Helmet>
                <BookingTimeslotsNew selectTimeslotHandler={props.selectTimeslotHandler} />
            </Route>
            <Route path="/booking/packages">
                <Helmet>
                    <title>{navigation.titleText(t("Booking packages"))}</title>
                </Helmet>
                <BookingPackagesNew />
            </Route>
            <Route path="/booking/competitions">
                <Helmet>
                    <title>{navigation.titleText(t("Booking competitions"))}</title>
                </Helmet>
                <BookingCompetitions />
            </Route>
            <Route path="/booking/upsells">
                <Helmet>
                    <title>{navigation.titleText(t("Booking upsells"))}</title>
                </Helmet>
                <BookingUpsells />
            </Route>
            <Route path="/booking/">
                <Helmet>
                    <title>{navigation.titleText(t("Booking"))}</title>
                </Helmet>
                <BookingIntroNew />
            </Route>
        </Switch>
    );
};

export default BookingPages;
